/**
 * Intake Field Mapping
 *
 * Defines how intake form fields map to destination tables (clients, assessment_responses).
 * Used by useClientIntake hook to write submitted data to the correct locations
 * and track field provenance in client_intake_submissions.
 */

// Fields that write directly to the clients table
export const CLIENT_TABLE_FIELDS = {
  name: { label: 'Legal Organization Name', column: 'name' },
  industry: { label: 'Industry', column: 'industry' },
  industry_sub_category: { label: 'Sub-Industry', column: 'industry_sub_category' },
  staffing_size: { label: 'Staffing Size', column: 'staffing_size' },
  it_team_size: { label: 'IT Team Size', column: 'it_team_size' },
  annual_revenue: { label: 'Annual Revenue Range', column: 'annual_revenue' },
  contact_name: { label: 'Primary Contact Name', column: 'contact_name' },
  contact_email: { label: 'Primary Contact Email', column: 'contact_email' },
  secondary_contact_name: { label: 'Secondary Contact Name', column: 'secondary_contact_name' },
  secondary_contact_email: { label: 'Secondary Contact Email', column: 'secondary_contact_email' },
  geographical_presence: { label: 'Geographical Presence', column: 'geographical_presence' }
}

// Fields that write to assessment_responses.business_scoping jsonb
// questionId maps to the admin assessment question so responses appear on both sides
export const BUSINESS_SCOPING_FIELDS = {
  security_team_size: { label: 'Security Team Size' },
  security_function_model: { label: 'Security Function Model', questionId: 'bs-28', responseType: 'select', valueMap: { 'in-house': 'Internal staff \u2014 IT managed entirely by in-house team', 'outsourced': 'Fully outsourced \u2014 IT managed by an MSP or third-party provider', 'hybrid': 'Hybrid \u2014 mix of internal IT staff and outsourced services' } },
  operational_hq: { label: 'Operational Headquarters', questionId: 'sl-1', responseType: 'select', stateField: true },
  office_location_states: { label: 'Office Locations (US States)', questionId: 'sl-2', responseType: 'multi-select', stateField: true },
  data_residency_states: { label: 'States Where You Store Resident Data', questionId: 'sl-3', responseType: 'multi-select', stateField: true },
  international_locations: { label: 'International Locations' },
  business_model: { label: 'Primary Business Model', questionId: 'bs-40', responseType: 'select' }
}

// Engagement context fields — written as ed-1/ed-2/ed-3 into business_scoping
export const ENGAGEMENT_CONTEXT_FIELDS = {
  engagement_drivers: { label: 'Engagement Drivers', questionId: 'ed-1' },
  engagement_drivers_other: { label: 'Engagement Drivers (Other)' },
  target_timeline: { label: 'Target Timeline', questionId: 'ed-2' },
  success_criteria: { label: 'Success Criteria', questionId: 'ed-3' },
  success_criteria_other: { label: 'Success Criteria (Other)' }
}

// Framework fields — tracked in client_intake_submissions columns
export const FRAMEWORK_FIELDS = {
  compliance_frameworks: { label: 'Compliance Frameworks' },
  custom_frameworks_raw: { label: 'Custom Frameworks (Unlisted)' }
}

// All intake field keys (used for provenance tracking)
export const ALL_INTAKE_FIELDS = [
  ...Object.keys(CLIENT_TABLE_FIELDS),
  ...Object.keys(BUSINESS_SCOPING_FIELDS),
  ...Object.keys(ENGAGEMENT_CONTEXT_FIELDS),
  ...Object.keys(FRAMEWORK_FIELDS)
]

/**
 * Build field provenance metadata for all submitted fields
 * @param {Object} formData - The intake form data
 * @returns {Object} Field provenance object for client_intake_submissions
 */
export function buildFieldProvenance(formData) {
  const now = new Date().toISOString()
  const provenance = {}

  for (const key of ALL_INTAKE_FIELDS) {
    const value = formData[key]
    // Only track fields that have a value
    if (value !== null && value !== undefined && value !== '') {
      provenance[key] = {
        source: 'client_intake',
        submitted_at: now,
        modified_by_admin: false,
        modified_at: null
      }
    }
  }

  return provenance
}

/**
 * Extract clients table update payload from form data
 * @param {Object} formData - The intake form data
 * @returns {Object} Payload for supabase clients table update
 */
export function extractClientTablePayload(formData) {
  const payload = {}
  for (const [key, mapping] of Object.entries(CLIENT_TABLE_FIELDS)) {
    const value = formData[key]
    if (value !== undefined) {
      payload[mapping.column] = value || null
    }
  }
  return payload
}

/**
 * Extract business scoping fields from form data.
 * Writes both the intake field key (for the intake form to read back)
 * and the admin assessment question ID (so admin assessment displays the response).
 * @param {Object} formData - The intake form data
 * @returns {Object} Business scoping fields to merge into assessment_responses
 */
export function extractBusinessScopingPayload(formData) {
  const payload = {}
  for (const [key, mapping] of Object.entries(BUSINESS_SCOPING_FIELDS)) {
    const value = formData[key]
    if (value !== undefined) {
      payload[key] = value || null

      // Also write under the admin assessment question ID so it appears on the admin side
      if (mapping.questionId && value) {
        if (mapping.responseType === 'multi-select') {
          // Multi-select: store as { value: [...], weight: 1 }
          let arr = Array.isArray(value) ? value : [value]
          // Translate full state names → abbreviations for state fields (matches businessScoping.json)
          if (mapping.stateField) {
            arr = arr.map(s => STATE_NAME_TO_ABBREV[s] || s)
          }
          payload[mapping.questionId] = { value: arr, weight: 1 }
        } else {
          // Single select: translate via valueMap or state abbreviation, else store raw value
          let mappedValue = value
          if (mapping.valueMap) {
            mappedValue = mapping.valueMap[value] || value
          } else if (mapping.stateField) {
            mappedValue = STATE_NAME_TO_ABBREV[value] || value
          }
          payload[mapping.questionId] = { value: mappedValue, weight: 1 }
        }
      }
    }
  }
  return payload
}

// Note: Engagement context is now written as ed-1/ed-2/ed-3 directly into business_scoping
// by useClientIntake.submitIntake(). No separate extraction needed.

// Staffing size options — values match EditClientModal
export const STAFFING_SIZE_OPTIONS = [
  { value: '1\u201315', label: '1\u201315 employees' },
  { value: '16\u201350', label: '16\u201350 employees' },
  { value: '51\u2013250', label: '51\u2013250 employees' },
  { value: '250+', label: '250+ employees' }
]

// IT team size options — matches EditClientModal
export const IT_TEAM_SIZE_OPTIONS = [
  { value: '0', label: 'No dedicated IT' },
  { value: '1-5', label: '1-5 people' },
  { value: '6-20', label: '6-20 people' },
  { value: '21-50', label: '21-50 people' },
  { value: '50+', label: '50+ people' }
]

// Security team size options for intake form
export const SECURITY_TEAM_SIZE_OPTIONS = [
  '0 (No dedicated security staff)', '1-2', '3-5', '6-10', '10+'
]

// Security function model options
export const SECURITY_FUNCTION_MODEL_OPTIONS = [
  { value: 'in-house', label: 'In-house' },
  { value: 'outsourced', label: 'Outsourced' },
  { value: 'hybrid', label: 'Hybrid (Both)' }
]

// Annual revenue options — values match EditClientModal
export const ANNUAL_REVENUE_OPTIONS = [
  { value: 'Under $25M', label: 'Under $25M' },
  { value: '$25M \u2013 $50M', label: '$25M \u2013 $50M' },
  { value: '$50M \u2013 $100M', label: '$50M \u2013 $100M' },
  { value: '$100M \u2013 $250M', label: '$100M \u2013 $250M' },
  { value: '$250M \u2013 $500M', label: '$250M \u2013 $500M' },
  { value: 'Over $500M', label: 'Over $500M' }
]

// Geographical presence options — value matches EditClientModal/DB, label matches bs-5 display
export const GEOGRAPHICAL_PRESENCE_OPTIONS = [
  { value: 'domestic', label: 'Domestic Only (US)' },
  { value: 'international', label: 'Some International Operations' },
  { value: 'global', label: 'Global / Multi-Jurisdiction' }
]

// Business model / primary customer base options (mirrors bs-40 in businessScoping.json)
export const BUSINESS_MODEL_OPTIONS = [
  { value: 'Business-to-Business (B2B)', label: 'B2B (Business-to-Business)' },
  { value: 'Business-to-Consumer (B2C)', label: 'B2C (Business-to-Consumer)' },
  { value: 'Both B2B and B2C', label: 'Both B2B and B2C' },
  { value: 'Government / Public Sector', label: 'Government / Public Sector' }
]

// Note: Target timeline options are now sourced directly from businessScoping.json (ed-2)

// State name → abbreviation map (matches businessScoping.json sl-2/sl-3 option values)
export const STATE_NAME_TO_ABBREV = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR',
  'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
  'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI', 'Idaho': 'ID',
  'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
  'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
  'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
  'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
  'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
  'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT',
  'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV',
  'Wisconsin': 'WI', 'Wyoming': 'WY', 'District of Columbia': 'DC'
}

// US states for multi-select dropdowns
export const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming', 'District of Columbia'
]
